import { useEffect, useRef } from 'react';
import { pushContinue } from '../api/services';

let ytApiPromise = null;
const loadYouTubeApi = () => {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve) => {
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(tag);
    window.onYouTubeIframeAPIReady = () => resolve(window.YT);
  });
  return ytApiPromise;
};

/**
 * Attach a YouTube IFrame API player to an iframe and report progress every 5s
 * via /api/continue. Saves to backend even for guests (no-op on failure).
 *
 * @param {string} elementId - iframe element ID to bind
 * @param {object} itemMeta - { title, tmdb_id|show_id, media_type, poster_path, backdrop_path, youtube_id }
 * @param {boolean} enabled - whether to track
 */
export default function useYouTubeProgress(elementId, itemMeta, enabled = true) {
  const playerRef = useRef(null);
  const timerRef = useRef(null);
  const lastReportRef = useRef(0);

  useEffect(() => {
    if (!enabled || !elementId || !itemMeta) return;
    let disposed = false;

    loadYouTubeApi().then((YT) => {
      if (disposed) return;
      try {
        playerRef.current = new YT.Player(elementId, {
          events: {
            onReady: () => {
              timerRef.current = window.setInterval(() => {
                try {
                  const p = playerRef.current;
                  if (!p || !p.getCurrentTime || !p.getDuration) return;
                  const cur = p.getCurrentTime();
                  const dur = p.getDuration();
                  if (!dur || dur < 5) return;
                  const progress = Math.max(0, Math.min(1, cur / dur));
                  // Rate-limit: only push if progress advanced by ≥ 3%
                  if (Math.abs(progress - lastReportRef.current) < 0.03) return;
                  lastReportRef.current = progress;
                  pushContinue({
                    ...itemMeta,
                    progress,
                    duration_sec: Math.round(dur),
                  }).catch(() => {});
                } catch {}
              }, 5000);
            },
            onStateChange: (e) => {
              // 0 = ended
              if (e.data === 0) {
                pushContinue({ ...itemMeta, progress: 1, duration_sec: Math.round(playerRef.current?.getDuration?.() || 0) }).catch(() => {});
              }
            },
          },
        });
      } catch {}
    });

    return () => {
      disposed = true;
      if (timerRef.current) clearInterval(timerRef.current);
      try { playerRef.current && playerRef.current.destroy && playerRef.current.destroy(); } catch {}
      playerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementId, enabled, itemMeta?.tmdb_id, itemMeta?.show_id, itemMeta?.youtube_id]);
}
